import type { Uuid } from "spacetimedb";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { geocodeAddress } from "@/web/lib/geocoding";
import type { TargetablePropertyForUserRow } from "@tycho/web-bindings/types";
import {
  useSuspendedOperatingProperties,
  useSuspendedTargetableProperties,
} from "@/web/routes/_protected/smb/-dashboard/dashboardData";
import { CATSKILLS_PROPERTIES } from "@/web/routes/explore/catskills/-data";
import type { WillowProperty, WillowPropertyTag } from "@/web/routes/explore/-types";
import { catskillsExternalId } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/constants";
import type {
  ExplorePin,
  ExplorePinCategory,
} from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/types";

/** First recognized Catskills tag wins; no recognized tag → off-market. */
function categoryFromTags(tags: WillowPropertyTag[]): ExplorePinCategory {
  if (tags.includes("FOR_LEASE") && !tags.includes("FOR_SALE")) return "forLease";
  if (tags.includes("FOR_SALE")) return "forSale";
  return "offMarket";
}

function catskillsPin(p: WillowProperty): ExplorePin {
  return {
    key: catskillsExternalId(p),
    category: categoryFromTags(p.tags),
    latitude: p.latitude,
    longitude: p.longitude,
    address: p.address,
    imageUrl: p.imageUrl,
    askingPrice: p.askingPrice,
    lastSalePrice: p.priorSale?.price,
    sqft: p.sqft,
    source: { kind: "catskills", externalId: catskillsExternalId(p), imageUrl: p.imageUrl },
  };
}

/** Selection key for a confirmed operating location (also the catalog row id). */
function operatingPinKey(targetablePropertyId: Uuid): string {
  return `op:${targetablePropertyId.toString()}`;
}

/**
 * The set of properties the Explore map plots: the static Catskills catalog plus
 * the user's confirmed operating locations (geocoded client-side from their
 * address via Mapbox, since the BFF row carries no coordinates). Already-targeted
 * properties are filtered out via `excludeTargetablePropertyIds` so the map only
 * offers *new* picks.
 *
 * `isResolving` is true while operating-location coordinates are still being
 * fetched — the Catskills pins are available immediately regardless.
 */
export function useExplorePins({
  brandId,
  excludeTargetablePropertyIds,
}: {
  brandId: Uuid | null;
  excludeTargetablePropertyIds?: ReadonlySet<string>;
}): { pins: ExplorePin[]; isResolving: boolean } {
  const operatingProperties = useSuspendedOperatingProperties();
  // The catalog tells us which targetable-property id a confirmed operating
  // location became, so we can both target it on confirm and exclude it when
  // it's already targeted.
  const catalog = useSuspendedTargetableProperties();

  // Confirmed operating locations for this brand, paired with their catalog row
  // (matched by googlePlaceId) so we have a targetable-property id to use, and
  // skipping any that are already targeted.
  const operatingForBrand = useMemo(() => {
    if (brandId === null) return [];
    const excluded = excludeTargetablePropertyIds ?? new Set<string>();
    const catalogByPlaceId = new Map<string, TargetablePropertyForUserRow>();
    for (const row of catalog) {
      if (row.brand.compareTo(brandId) === 0 && row.googlePlaceId !== undefined) {
        catalogByPlaceId.set(row.googlePlaceId, row);
      }
    }
    return operatingProperties
      .filter((p) => p.brand.compareTo(brandId) === 0 && p.confirmed)
      .map((p) => ({ operating: p, catalogRow: catalogByPlaceId.get(p.googlePlaceId) }))
      .filter(
        (pair) => pair.catalogRow !== undefined && !excluded.has(pair.catalogRow.id.toString()),
      );
  }, [operatingProperties, catalog, brandId, excludeTargetablePropertyIds]);

  // Geocode each operating location by address. Cached forever per address; the
  // Catskills pins don't depend on these resolving.
  const geocodeResults = useQueries({
    queries: operatingForBrand.map(({ operating }) => ({
      queryKey: ["explorePinGeocode", operating.id.toString()] as const,
      queryFn: () => geocodeAddress(operating.canonicalAddress),
      staleTime: Infinity,
    })),
  });

  const isResolving = geocodeResults.some((q) => q.isPending);

  return useMemo(() => {
    const catskillsPins = CATSKILLS_PROPERTIES.map(catskillsPin);

    const operatingPins = operatingForBrand.flatMap(
      ({ operating, catalogRow }, i): ExplorePin[] => {
        if (!catalogRow) return [];
        const coords = geocodeResults[i]?.data;
        if (!coords) return [];
        return [
          {
            key: operatingPinKey(catalogRow.id),
            category: "operatingLocation",
            latitude: coords.latitude,
            longitude: coords.longitude,
            address: operating.canonicalAddress,
            imageUrl: catalogRow.imageUrl,
            googlePlaceId: catalogRow.googlePlaceId ?? operating.googlePlaceId,
            googlePlacePhotoName: catalogRow.googlePlacePhotoName,
            source: { kind: "operating", targetablePropertyId: catalogRow.id },
          },
        ];
      },
    );

    return { pins: [...operatingPins, ...catskillsPins], isResolving };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- geocodeResults identity churns; gate on isResolving
  }, [operatingForBrand, isResolving]);
}
